'use client';

import { useEffect, useRef, type ChangeEvent, type KeyboardEvent } from 'react';
import { X } from '@phosphor-icons/react';
import { PhoneIcon } from '@/components/icons';

function phoneDigits(value: string): string {
  const digits = value.replace(/\D/g, '');
  // Russian mobile numbers start with 9; 7 and 8 are country/trunk prefixes.
  return /^[78]/.test(digits) ? digits.slice(1, 11) : digits.slice(0, 10);
}

export function formatPhone(value: string): string {
  const digits = phoneDigits(value);
  if (!digits) return '+7 (';
  if (digits.length <= 3) return `+7 (${digits}`;
  if (digits.length <= 6) return `+7 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 8) {
    return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
}

export function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 && /^[78]/.test(digits)) return `7${digits.slice(1)}`;
  if (digits.length === 10) return `7${digits}`;
  return digits;
}

export function isRussianMobile(normalized: string): boolean {
  return /^79\d{9}$/.test(normalized);
}

function phoneCaretPosition(formatted: string, nationalDigitsBeforeCaret: number): number {
  if (nationalDigitsBeforeCaret <= 0) return 4;
  let count = 0;
  for (let index = 4; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index])) count += 1;
    if (count === nationalDigitsBeforeCaret) return index + 1;
  }
  return formatted.length;
}

type AuthPhoneFieldProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
};

export function AuthPhoneField({ value, onChange, disabled, error }: AuthPhoneFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const caretFrameRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (caretFrameRef.current !== null) cancelAnimationFrame(caretFrameRef.current);
    },
    []
  );

  const updateValue = (rawValue: string, caret: number) => {
    const formatted = formatPhone(rawValue);
    const hasPrefix = /^[78]/.test(rawValue.replace(/\D/g, ''));
    const digitsBeforeCaret = rawValue.slice(0, caret).replace(/\D/g, '').length;
    const nextCaret = phoneCaretPosition(formatted, digitsBeforeCaret - Number(hasPrefix));
    onChange(formatted);
    if (caretFrameRef.current !== null) cancelAnimationFrame(caretFrameRef.current);
    caretFrameRef.current = requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input && document.activeElement === input) input.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateValue(
      event.currentTarget.value,
      event.currentTarget.selectionStart ?? event.currentTarget.value.length
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    const input = event.currentTarget;
    const start = input.selectionStart ?? 0;
    if (start !== input.selectionEnd) return;

    // Skip mask separators when deleting, so the caret never gets stuck on a dash.
    const direction = event.key === 'Backspace' ? -1 : 1;
    let index = direction === -1 ? start - 1 : start;
    while (index >= 4 && index < input.value.length && !/\d/.test(input.value[index])) {
      index += direction;
    }
    if (index < 4) {
      event.preventDefault();
      input.setSelectionRange(4, 4);
      return;
    }
    const adjacentIndex = direction === -1 ? start - 1 : start;
    if (index !== adjacentIndex && index < input.value.length) {
      event.preventDefault();
      updateValue(
        input.value.slice(0, index) + input.value.slice(index + 1),
        direction === -1 ? index : start
      );
    }
  };

  const clearPhone = () => {
    inputRef.current?.focus({ preventScroll: true });
    updateValue('', 0);
  };

  return (
    <div className="auth-ui-phone-field" data-invalid={!!error}>
      <label className="auth-ui-field-label" htmlFor="auth-phone">
        Номер телефона
      </label>
      <div className="auth-ui-phone-control">
        <PhoneIcon className="auth-ui-phone-icon" size={22} aria-hidden="true" />
        <input
          ref={inputRef}
          id="auth-phone"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className="auth-ui-phone-input"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? 'auth-phone-error' : undefined}
          placeholder="+7 (999) 123-45-67"
          style={{ fontSize: 16 }}
        />
        {phoneDigits(value).length > 0 && (
          <button
            type="button"
            className="auth-ui-phone-clear"
            aria-label="Очистить номер телефона"
            onClick={clearPhone}
            disabled={disabled}
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        )}
      </div>
      {error && (
        <p className="auth-ui-field-error" id="auth-phone-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
